import { useMemo, useState } from 'react';
import { Search, FileText, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { usePrograms, programStatus, CATEGORY_LABELS, type Program } from '@/data/hooks/usePrograms';
import { countdownLabel } from '@/lib/programStatus';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export function AttachProgramDialog({
  open, onOpenChange, dealId, dealershipId, attachedIds, onAttached,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  dealId: string;
  dealershipId: string;
  attachedIds: Set<string>;
  onAttached: () => void;
}) {
  const { mode, userId } = useAuth();
  const { programs } = usePrograms();
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return programs
      .filter(p => programStatus(p) !== 'expired')
      .filter(p => !attachedIds.has(p.id))
      .filter(p => !needle || (p.title + ' ' + (p.summary ?? '')).toLowerCase().includes(needle))
      .slice(0, 50);
  }, [programs, q, attachedIds]);

  const attach = async (p: Program) => {
    setBusy(p.id);
    try {
      if (mode === 'demo') {
        toast.success(`Attached "${p.title}" (demo)`);
        onAttached(); onOpenChange(false); return;
      }
      const { error } = await supabase.from('deal_programs').insert({
        deal_id: dealId, program_id: p.id, dealership_id: dealershipId, attached_by: userId,
      });
      if (error) throw error;
      // Log activity on the deal timeline
      await supabase.from('activities').insert({
        deal_id: dealId, dealership_id: dealershipId, rep_id: userId,
        activity_type: 'note_added', customer_name: null,
        details: `Attached program: ${p.title}`,
      });
      toast.success(`Attached "${p.title}"`);
      onAttached();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message ?? 'Could not attach program');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Attach a program</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search programs…" autoFocus />
        </div>
        <div className="max-h-[420px] space-y-1.5 overflow-y-auto">
          {filtered.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {programs.length === 0 ? 'No programs available yet.' : 'No matches.'}
            </div>
          )}
          {filtered.map(p => (
            <button
              key={p.id}
              onClick={() => attach(p)}
              disabled={busy === p.id}
              className="flex w-full items-start gap-3 rounded-lg border border-border/40 p-3 text-left transition hover:border-primary/50 hover:bg-muted/30 disabled:opacity-50"
            >
              <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium">{p.title}</span>
                  {busy === p.id && <Loader2 className="h-3 w-3 animate-spin" />}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
                  <span>{CATEGORY_LABELS[p.category]}</span>
                  {countdownLabel(p) && <span>· {countdownLabel(p)}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
        <div className="flex justify-end">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}